/**
 * AIEnablement.jsx
 * ---------------------------------------------------------------------------
 * Section 6. AI enablement for legal teams — drafting frameworks, prompt
 * engineering, risk-aware implementation, and training.
 */

import React from "react";
import Section from "../ui/Section.jsx";
import { H2, Lede } from "../ui/Heading.jsx";
import Button from "../ui/Button.jsx";
import { tokens } from "../../lib/tokens.js";

const FEATURES = [
  [
    "AI-assisted drafting frameworks",
    "Controlled templates and prompt libraries aligned to firm standards.",
  ],
  [
    "Prompt engineering for legal workflows",
    "Purpose-built prompts for research, review, and summarization.",
  ],
  [
    "Risk-aware implementation",
    "Privilege, confidentiality, and professional responsibility by design.",
  ],
  [
    "Staff and attorney training",
    "Structured programs with measurable competency outcomes.",
  ],
];

export default function AIEnablement() {
  return (
    <Section id="services" label="V · AI Enablement" tone="paper">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 96,
          alignItems: "start",
        }}
      >
        <div>
          <H2>AI enablement for legal teams.</H2>
          <Lede>
            Integrate AI into legal workflows with precision and control.
            Improve drafting, research, and internal coordination without
            compromising confidentiality or professional responsibility.
          </Lede>
          <div style={{ marginTop: 48 }}>
            <Button
              onClick={() =>
                document
                  .getElementById("firms")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Request AI Training
            </Button>
          </div>
        </div>

        <div>
          {FEATURES.map(([title, body], i) => (
            <div
              key={title}
              style={{
                padding: "28px 0",
                borderTop: `1px solid ${tokens.divider}`,
                borderBottom:
                  i === FEATURES.length - 1
                    ? `1px solid ${tokens.divider}`
                    : "none",
                display: "grid",
                gridTemplateColumns: "1fr 1.4fr",
                gap: 32,
                alignItems: "baseline",
              }}
            >
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 22,
                  color: tokens.ink,
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {title}
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 14,
                  color: tokens.inkSoft,
                  lineHeight: 1.6,
                }}
              >
                {body}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}
